import * as yup from "yup";
import Costing from "./steps/Costing";
import { bool } from "prop-types";
const isEmpty = (v) => v === undefined || v === null || (typeof v === "string" && v.trim() === "");

const optString = () => yup.string().nullable().notRequired();
export const ocfSchema = yup.object({
  applicant: yup.object({
    name: yup.string().trim().required("Customer name is required"),
    careOfName: yup.string().trim().required("Father/Husband is required"),
    gender: yup.string().required("Gender is required"),
    email: yup.string().email("Invalid email").required("Email is required"),
    mobile: yup
      .string()
      .matches(/^[6-9]\d{9}$/, "Enter valid mobile")
      .required("Mobile is required"),
    mobile2: yup.string().nullable(),
    dob: yup.string().required("DOB is required"),
    address: yup.string().required("Address is required"),
    profession: yup.string().nullable(),
    aadhar: yup
      .string()
      .matches(/^\d{12}$/, "Aadhar must be 12 digits")
      .required("Aadhar required"),
    pan: yup
      .string()
      .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN")
      .required("PAN required"),
    aadharFile: yup.mixed().required("Aadhar copy required"),
    panFile: yup.mixed().required("PAN copy required"),
  }),
  coApplicant: yup
    .object({
      name: optString(),
      careOfName: optString(),
      gender: optString(),
      dob: optString(),
      address: optString(),

      // optional
      email: optString().email("Invalid email"),

      mobile: optString().matches(/^[6-9]\d{9}$/, {
        message: "Enter valid mobile",
        excludeEmptyString: true,
      }),

      mobile2: optString().matches(/^[6-9]\d{9}$/, {
        message: "Enter valid alternate mobile",
        excludeEmptyString: true,
      }),

      profession: optString(),

      aadhar: optString().matches(/^\d{12}$/, {
        message: "Aadhar must be 12 digits",
        excludeEmptyString: true,
      }),

      pan: optString().matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, {
        message: "Invalid PAN",
        excludeEmptyString: true,
      }),
      relationWithApplicant: optString(),
      aadharFile: yup.mixed().nullable().notRequired(),
      panFile: yup.mixed().nullable().notRequired(),
    })

    .test("coApplicant-complete", function (value = {}) {
      // ⭐ ONLY mandatory fields here
      const mandatoryKeys = [
        "name",
        "careOfName",
        "gender",
        "mobile",
        "dob",
        "address",
        "aadhar",
        "pan",
        "aadharFile",
        "panFile",
        "relationWithApplicant",
      ];

      // check if user filled ANY field at all
      const hasAny = Object.values(value).some((v) => !isEmpty(v));

      // completely empty → skip validation
      if (!hasAny) return true;

      // collect ALL errors
      const errors = mandatoryKeys
        .filter((k) => isEmpty(value[k]))
        .map((k) =>
          this.createError({
            path: `coApplicant.${k}`,
            message: "Required",
          })
        );

      if (errors.length) {
        throw new yup.ValidationError(errors);
      }

      return true;
    })
    .nullable(),

  property: yup.object({
    name: yup.string().trim().required("Property name is required"),
    // typeSize: yup.object({
    //   type: yup.string().required("Type is required"),
    //   size: yup
    //     .number()
    //     .typeError("Size must be a number")
    //     .required("Size is required")
    //     .min(1, "Size must be greater than 0"),
    // }),
    type: yup.string().required("Type is required"),
    size: yup.string().required("Size is required"),
    paymentPlan: yup.string().required("Payment plan is required"),
    // category: yup.string().required("Category is required"),
    // sizeUnit: yup.string().required("Size unit is required"),
    // commissionType: yup.string().required("Commission type is required"),
    /* Pricing */
    bsp: yup.number().typeError("BSP must be number").required("BSP is required").min(0),
    otherCharges: yup
      .number()
      .typeError("Other charges must be number")
      .required("Other charges required")
      .min(0),
    otherChargesGst: yup
      .number()
      .typeError("Other charges GST must be number")
      .required("Other charges GST required")
      .min(0),
    commission: yup.number().typeError("Revenue must be number").required("Revenue is required."),
    plc: yup.string().required("PLC is required"),
    isDiscount: yup.boolean().default(false).required("Discount preference is required"),
    // discount: yup
    //   .number()
    //   .nullable()
    //   .when("isDiscount", {
    //     is: true,
    //     then: (s) =>
    //       s.typeError("Discount must be number").required("Revenue discount required").min(0),
    //     otherwise: (s) => s.notRequired().nullable(),
    //   }),
    discount: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null ? 0 : value
      )
      .default(0)
      .when("isDiscount", {
        is: true,
        then: (s) =>
          s
            .typeError("Discount must be a number")
            .required("Revenue discount required")
            .min(0, "Discount cannot be negative"),
        otherwise: (s) =>
          s
            .notRequired()
            .default(0)
            .transform(() => 0), // force 0 when discount not enabled
      }),
    commissionDiscountUnit: yup
      .string()
      .nullable()
      .when("isDiscount", {
        is: true,
        then: (s) => s.required("Discount unit required"),
        otherwise: (s) => s.notRequired(),
      }),
    dis_approval_file: yup
      .mixed()
      .nullable()
      // .when("discount", {
      //   is: (val) => Number(val) > 0, // ⭐ key logic
      //   then: (s) => s.required("Discount approval file is required"),
      //   otherwise: (s) => s.notRequired().nullable(),
      // }),
      .when("isDiscount", {
        is: true,
        then: (s) => s.typeError("Discount must be number").required("Revenue discount required"),
        otherwise: (s) => s.notRequired().nullable(),
      }),
    unitNo: yup.string().required("Unit No required"),
    floorNo: yup.string().required("Floor No required"),
    towerNo: yup.string().required("Tower No required"),
  }),

  costing: yup.object({
    totalBsp: yup
      .number()
      .typeError("Total BSP must be a number")
      .required("Total BSP is required")
      .min(0, "Cannot be negative"),
    propertyPrice: yup
      .number()
      .typeError("Property price must be a number")
      .required("Property price is required")
      .min(0, "Cannot be negative"),
    netRevenue: yup
      .number()
      .typeError("Commission must be a number")
      .required("Commission is required")
      .min(0, "Cannot be negative"),
    salesPersonRemark: yup.string().typeError("Remark must be a string").nullable(),
    totalCommission: yup
      .number()
      .typeError("Net income must be a number")
      .required("Net income is required")
      .min(0, "Cannot be negative"),
    totalDiscount: yup
      .number()
      .typeError("Net income must be a number")
      .required("Net income is required")
      .min(0, "Cannot be negative"),
    // bookingForm: yup.mixed().required("Booking form file is required"),
    // bookingForm: yup
    //   .array()
    //   .of(
    //     yup.object().shape({
    //       file: yup.mixed().test("fileRequired", "Booking form file is required", (value) => {
    //         return value && (value instanceof File || value?.message);
    //       }),
    //     })
    //   )
    //   .min(1, "At least one booking form is required")
    //   .required("Booking form is required"),
    bookingForm: yup
      .array()
      .of(
        yup.object().shape({
          file: yup
            .mixed()
            .nullable() // ⭐ allow null first
            .test("fileRequired", "Booking form file is required", (value) => {
              return value && (value instanceof File || value?.message);
            }),
        })
      )
      .min(1, "At least one booking form is required")
      .required("Booking form is required"),
  }),

  payment: yup
    .array()
    .of(
      yup.object({
        type: yup.string().required("Type is required"),

        amount: yup
          .number()
          .typeError("Amount must be number")
          .required("Amount is required")
          .min(0, "Cannot be negative"),

        receiptNo: yup.string().required("Payment Detail  is required"),
        paymentProof: yup.mixed().required("Payment proof file is required"),
        confirmation: yup.boolean().default(false).oneOf([true], "Please accept the confirmation"),
        // builderConfirmationFile: yup.mixed().required("Builder confirmation file is required"),
        paymentDate: yup.string().required("Date is required"),
        builderConfirmationFile: yup.mixed().when("confirmation", {
          is: true,
          then: (schema) => schema.required("Builder confirmation file is required"),
          otherwise: (schema) => schema.nullable().notRequired(),
        }),
      })
    )
    .min(1, "At least one payment required"),
  // futurePaymentsForm: yup
  //   .array()
  //   .of(
  //     yup.object({
  //       expectedDate: yup.string().required("Excpected Date is required ."),
  //       percentage: yup.number().required("Percentage is required ."),
  //       billingAllowed: yup.boolean().default(false).oneOf([true], ""),
  //       bbaSigned: yup.boolean().default(false),
  //       // .oneOf([true], "BBA signed is required"),
  //       bbaSignedDate: yup.string().required("Expected Date is required ."),
  //     })
  //   )
  //   .min(1, "AtLeast One is required"),
});

export const stepFieldMap = {
  0: [
    "applicant.name",
    "applicant.careOfName",
    "applicant.gender",
    "applicant.email",
    "applicant.mobile",
    "applicant.mobile2",
    "applicant.dob",
    "applicant.address",
    "applicant.aadharFile",
    "applicant.aadhar",
    "applicant.pan",
    "applicant.panFile",
  ],
  1: [
    "coApplicant.name",
    "coApplicant.gender",
    "coApplicant.mobile",
    "coApplicant.mobile2",
    "coApplicant.email",
    "coApplicant.dob",
    "coApplicant.address",
    "coApplicant.pan",
    "coApplicant.panFile",
    "coApplicant.aadhar",
    "coApplicant.aadharFile",
    "coApplicant.relationWithApplicant",
  ],

  2: [
    "property.name",
    "property.type",
    "property.size",
    "property.paymentPlan",
    "property.category",
    "property.sizeUnit",
    "property.commissionType",
    "property.bsp",
    "property.bspGst",
    "property.otherCharges",
    "property.otherChargesGst",
    "property.revenuePercentage",
    "property.plc",
    "property.isDiscount",
    "property.revenueDiscount",
    "property.commission",
    "property.discount",
    "property.com_unit",
    "property.dis_unit",
    "property.net_income",
    "property.dis_approval_file",
    "property.unitNo",
    "property.floorNo",
    "property.towerNo",
  ],
  3: [
    "costing.totalBsp",
    "costing.propertyPrice",
    "costing.netRevenue",
    "costing.bookingForm",
    "costing.totalCommission",
    "costing.totalDiscount",
    "costing.remark",
  ],
  4: ["payment"],
  5: [""],
  6: [""],
  7: [""],
};

const getNestedError = (obj, path) => path.split(".").reduce((acc, key) => acc?.[key], obj);

// export const hasStepError = (stepIndex, errors) => {
//   return stepFieldMap[stepIndex].some((field) => getNestedError(errors, field));
// };

export const hasStepError = (stepIndex, errors) => {
  const fields = stepFieldMap[stepIndex];

  const hasFieldError = fields.some((field) => getNestedError(errors, field));

  const parentKey = fields[0]?.split(".")[0]; // coApplicant
  const hasObjectError = errors?.[parentKey]?.message;

  return hasFieldError || hasObjectError;
};

export const numericRules = (min = 1, max = 12, fieldName = "Field", isRequired = true) => ({
  ...(isRequired && { required: `${fieldName} is required` }),
  validate: (value) => {
    if (!value) {
      return isRequired ? `${fieldName} is required` : true;
    }
    if (!/^[0-9]+$/.test(value)) {
      return "Only numbers allowed";
    }
    if (value.length < min) {
      return `Minimum ${min} digits required`;
    }
    if (value.length > max) {
      return `Maximum ${max} digits allowed`;
    }
    return true;
  },
});

export const panRules = {
  required: "PAN is required",
  pattern: {
    value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
    message: "Invalid PAN format (ABCDE1234F)",
  },
};

// export const reviewSchema = yup.object({
//   futurePaymentsForm: yup.object({
//     payments: yup
//       .array()
//       .of(
//         yup.object({
//           expectedDate: yup.string().required("Expected Date is required"),

//           percentage: yup
//             .number()
//             .typeError("Percentage required")
//             .required("Percentage required")
//             .min(1)
//             .max(100),

//           billingAllowed: yup.boolean(),
//         })
//       )
//       .min(1, "At least one payment required")
//       .test("one-billable-required", "Please select one Billable payment", function (payments) {
//         return payments?.some((p) => p.billingAllowed === true);
//       }),

//     bbaSigned: yup.boolean(),

//     bbaSignedDate: yup.string().when("bbaSigned", {
//       is: true,
//       then: () => yup.string().required("BBA date required"),
//       otherwise: () => yup.string().nullable(),
//     }),

//     remark: yup.string().when("action", {
//       is: "REJECT",
//       then: (schema) => schema.required("Remark is required when rejecting"),
//       otherwise: (schema) => schema.notRequired(),
//     }),
//   }),
// });

// export const reviewSchema = yup.object({
//   futurePaymentsForm: yup.object({
//     action: yup.string().required(),

//     payments: yup.array().when("action", {
//       is: (action) => action !== "REJECT",
//       then: () =>
//         yup
//           .array()
//           .of(
//             yup.object({
//               expectedDate: yup.string().required("Expected Date is required"),

//               percentage: yup
//                 .number()
//                 .typeError("Percentage required")
//                 .required("Percentage required")
//                 .min(1)
//                 .max(100),

//               billingAllowed: yup.boolean(),
//             })
//           )
//           .min(1, "At least one payment required")
//           .test("one-billable-required", "Please select one Billable payment", function (payments) {
//             return payments?.some((p) => p.billingAllowed === true);
//           }),

//       otherwise: () => yup.array().notRequired(),
//     }),

//     bbaSigned: yup.boolean(),

//     bbaSignedDate: yup.string().when(["bbaSigned", "action"], {
//       is: (bbaSigned, action) => bbaSigned === true && action !== "REJECT",
//       then: () => yup.string().required("BBA date required"),
//       otherwise: () => yup.string().notRequired(),
//     }),

//     remark: yup.string().when("action", {
//       is: "REJECT",
//       then: () => yup.string().required("Remark is required when rejecting"),
//       otherwise: () => yup.string().notRequired(),
//     }),

//     isRecordingNotPresent: yup.boolean(),

//     clientConfirmationRecording: yup.mixed().when(["isRecordingNotPresent", "action"], {
//       is: (isRecordingNotPresent, action) => action !== "REJECT" && !isRecordingNotPresent,
//       then: () => yup.mixed().required("Client confirmation recording is required"),
//       otherwise: () => yup.mixed().notRequired(),
//     }),

//     approvalForSkip: yup.string().when(["isRecordingNotPresent", "action"], {
//       is: (isRecordingNotPresent, action) => action !== "REJECT" && isRecordingNotPresent === true,
//       then: () =>
//         yup.string().required("Approval screenshot required when recording not available"),
//       otherwise: () => yup.string().notRequired(),
//     }),
//   }),
// });

// export const reviewSchema = yup.object({
//   futurePaymentsForm: yup.object().shape({
//     action: yup.string().required(),

//     payments: yup.array().when("action", {
//       is: (action) => action !== "REJECT",
//       then: (schema) =>
//         schema
//           .of(
//             yup.object({
//               expectedDate: yup.string().required("Expected Date is required"),
//               percentage: yup
//                 .number()
//                 .typeError("Percentage required")
//                 .required("Percentage required")
//                 .min(1)
//                 .max(100),
//               billingAllowed: yup.boolean(),
//             })
//           )
//           .min(1, "At least one payment required")
//           .test("one-billable-required", "Please select one Billable payment", (payments) =>
//             payments?.some((p) => p.billingAllowed)
//           ),
//       otherwise: (schema) => schema.notRequired(),
//     }),

//     bbaSigned: yup.boolean(),

//     bbaSignedDate: yup.string().when(["bbaSigned", "action"], {
//       is: (bbaSigned, action) => bbaSigned === true && action !== "REJECT",
//       then: (schema) => schema.required("BBA date required"),
//       otherwise: (schema) => schema.notRequired(),
//     }),

//     remark: yup.string().when("action", {
//       is: "REJECT",
//       then: (schema) => schema.required("Remark is required when rejecting"),
//       otherwise: (schema) => schema.notRequired(),
//     }),

//     isRecordingNotPresent: yup.boolean().default(false),

//     clientConfirmationRecording: yup.mixed().when(["isRecordingNotPresent", "action"], {
//       is: (isRecordingNotPresent, action) => action !== "REJECT" && isRecordingNotPresent === false,
//       then: (schema) => schema.required("Client confirmation recording is required"),
//       otherwise: (schema) => schema.notRequired(),
//     }),

//     approvalForSkip: yup.mixed().when(["isRecordingNotPresent", "action"], {
//       is: (isRecordingNotPresent, action) => action !== "REJECT" && isRecordingNotPresent === true,
//       then: (schema) =>
//         schema.required("Approval screenshot required when recording not available"),
//       otherwise: (schema) => schema.notRequired(),
//     }),
//   }),
// });

export const schema = yup.object({
  action: yup.string().required("Action is required"), // APPROVE / REJECT / SAVE

  // Payments required only on APPROVE
  payments: yup.array().when("action", {
    is: "APPROVE",
    then: (schema) =>
      schema
        .of(
          yup.object({
            expectedDate: yup.string().required("Expected Date is required"),
            percentage: yup
              .number()
              .typeError("Percentage required")
              .required("Percentage required")
              .min(1)
              .max(100),
            billingAllowed: yup.boolean(),
          })
        )
        .min(1, "At least one payment required")
        .test("one-billable-required", "Please select one Billable payment", (payments) =>
          payments?.some((p) => p.billingAllowed)
        ),
    otherwise: (schema) => schema.notRequired(),
  }),

  // BBA required only on APPROVE
  bbaSigned: yup.boolean().when("action", {
    is: "APPROVE",
    then: (schema) => schema.required("BBA Signed is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  bbaSignedDate: yup.string().when(["bbaSigned", "action"], {
    is: (bbaSigned, action) => bbaSigned === true && action === "APPROVE",
    then: (schema) => schema.required("BBA date required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  // Remark required only on REJECT
  remark: yup.string().when("action", {
    is: "REJECT",
    then: (schema) => schema.required("Remark is required when rejecting"),
    otherwise: (schema) => schema.notRequired(),
  }),

  isRecordingNotPresent: yup.boolean(),

  // Client confirmation required only on APPROVE and if recording present
  clientConfirmationRecording: yup
    .object()
    .nullable()
    .transform((value, originalValue, context) => {
      return context?.parent?.action === "REJECT" ? null : value;
    })
    .when(["isRecordingNotPresent", "action"], {
      is: (isRecordingNotPresent, action) => action === "APPROVE" && !isRecordingNotPresent,
      then: (schema) => schema.shape({ message: yup.string().required("Recording required") }),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  // Approval for skip required only on APPROVE if recording not present
  approvalForSkip: yup
    .object()
    .nullable()
    .when(["isRecordingNotPresent", "action"], {
      is: (isRecordingNotPresent, action) => action === "APPROVE" && isRecordingNotPresent,
      then: (schema) =>
        schema.shape({ message: yup.string().required("Approval screenshot required") }),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
});
